import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import getConfig from 'next/config';

import { logMarfeelEvent, isMarfeelDebugEnabled } from '@/utils/marfeelLogger';

const { publicRuntimeConfig } = getConfig();

/**
 * Tratativa de page-view e experiences do Marfeel para a SPA (Next.js).
 *
 * Contrato (alinhado à doc oficial do Marfeel JS SDK):
 * - 1ª página (initial mount): o SDK já auto-rastreia o primeiro page-view E auto-dispara
 *   as experiences ao carregar. NÃO chamamos nada aqui — senão a página de entrada contaria
 *   2× o page-view e teria "duplicate experience delivery".
 * - Navegação SPA (virtual navigation): por rota nova, disparamos 1× cada, nesta ordem:
 *     1. `compass.trackNewPage()`        — registra o page-view da nova rota;
 *     2. `experiences.triggerExperiences()` — recarrega as experiences. DEVE vir DEPOIS do
 *        trackNewPage (a URL do trackNewPage tem precedência na resolução).
 *   A dependência é `router.asPath` (muda a cada navegação client-side), e não mais um
 *   estado de transição consumido fora do seu Provider — que era uma dependência morta
 *   (sempre `undefined`) e fazia o effect nunca re-disparar em rota nova.
 *
 * O SDK do Marfeel só é injetado em produção (ver `_document.tsx`), então o disparo é
 * gated por env. Quando o SDK não está presente, o push em `window.marfeel.cmd` é inócuo.
 *
 * Os logs são observe-only e silenciosos por padrão (ligam via `?marfeelDebug=1`).
 *
 * @param enabled Se o Marfeel está habilitado para a rota atual (ver `useScriptsToRoute`).
 */
export function useMarfeelPageTracking(enabled: boolean): void {
  const router = useRouter();

  // refs de diagnóstico (observe-only) — não alteram o comportamento de tracking
  const isFirstRenderRef = useRef(true);
  const fireCountRef = useRef(0);

  const isProduction = publicRuntimeConfig.NEXT_PUBLIC_ENVIRONMENT === 'production';

  // probe de readiness do SDK (uma vez, só em debug): os callbacks de compass/experiences
  // só rodam quando o módulo correspondente terminou de carregar → confirma "SDK executou
  // nesta req" sem disparar tracking.
  useEffect(() => {
    if (!isProduction || !isMarfeelDebugEnabled()) return;

    window?.marfeel?.cmd?.push([
      'compass',
      function () {
        logMarfeelEvent('compass:ready', { route: router.asPath });
      },
    ]);
    window?.marfeel?.cmd?.push([
      'experiences',
      function () {
        logMarfeelEvent('experiences:ready', { route: router.asPath });
      },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const route = router.asPath;
    const isInitialMount = isFirstRenderRef.current;
    const phase = isInitialMount ? 'initial-mount' : 'virtual-navigation';
    // só dispara em navegação SPA: na 1ª req o SDK já auto-rastreia (evita contar 2×).
    const willPush = isProduction && enabled && !isInitialMount;
    fireCountRef.current += 1;

    logMarfeelEvent('effect:fire', {
      phase,
      fireCount: fireCountRef.current,
      route,
      useMarfeel: enabled,
      willPush,
    });

    isFirstRenderRef.current = false;

    if (!willPush) return;

    logMarfeelEvent('trackNewPage:queued', { phase, route });
    window?.marfeel?.cmd?.push([
      'compass',
      function (compass: MarfeelCompass) {
        logMarfeelEvent('trackNewPage:executed', { phase, route });
        compass.trackNewPage();
      },
    ]);

    // Experiences: recarrega as experiences na nova rota SPA. Empilhado DEPOIS do
    // trackNewPage (a URL dele tem precedência) e também gated por willPush — na 1ª carga
    // o SDK já dispara sozinho, e chamar aqui causaria "duplicate experience delivery".
    logMarfeelEvent('triggerExperiences:queued', { phase, route });
    window?.marfeel?.cmd?.push([
      'experiences',
      function (experiences: MarfeelExperiences) {
        logMarfeelEvent('triggerExperiences:executed', { phase, route });
        experiences.triggerExperiences();
      },
    ]);
  }, [router.asPath, enabled]);
}
